import { defineModule } from "direct-vuex";
import { moduleActionContext } from "@/store";
import { MailBoxListItem } from "@/models/api/administration/mailBox/mailBoxListItem";
import {
  getMailBoxList,
  getMailConfiguration,
  runTestConnection,
  lastReadMailUIDMessage,
  saveMailConfiguration,
  createMailConfiguration,
} from "@/requests/mailBox";
import { MailConfiguration } from "@/models/api/administration/mailBox/mailConfiguration";

export interface AdministrationMailBoxConfigurationStateInterface {
  isLoading: boolean;
  isAddDialog: boolean;
  mailBoxList: Array<MailBoxListItem>;
  mailBoxListCount: number | null;
  mailConfiguration: MailConfiguration | null;
  newMailConfiguration: MailConfiguration;
  recordStampId: number | null;
}

export const AdministrationMailBoxConfigurationState: AdministrationMailBoxConfigurationStateInterface = {
  isLoading: false,
  isAddDialog: false,
  mailBoxList: new Array<MailBoxListItem>(),
  mailBoxListCount: null,
  mailConfiguration: new MailConfiguration(),
  newMailConfiguration: new MailConfiguration(),
  recordStampId: null,
};

const AdministrationMailBoxConfigurationModule = defineModule({
  state: AdministrationMailBoxConfigurationState as AdministrationMailBoxConfigurationStateInterface,
  namespaced: true as const,
  getters: {},
  mutations: {
    setIsLoading(state, param: boolean) {
      state.isLoading = param;
    },
    setIsAddDialog(state, param: boolean) {
      state.isAddDialog = param;
    },
    setMailBoxList(state, param: Array<MailBoxListItem>) {
      state.mailBoxList = param;
    },
    setMailBoxListCount(state, param: number) {
      state.mailBoxListCount = param;
    },
    setMailConfiguration(state, param: MailConfiguration | null) {
      state.mailConfiguration = param;
    },
    setRecordStampId(state, param: number | null) {
      state.recordStampId = param;
    },
    setNewMailConfiguration(state, param: MailConfiguration) {
      state.newMailConfiguration = param;
    },
  },
  actions: {
    async clearNewMailBoxConfiguration(context) {
      const { commit } = _moduleActionContext(context);
      commit.setIsLoading(true);
      commit.setNewMailConfiguration(new MailConfiguration());
      commit.setIsLoading(false);
    },
    async saveMailBoxConfiguration(context) {
      const { commit, state } = _moduleActionContext(context);
      commit.setIsLoading(true);
      if (state.mailConfiguration !== null) {
        await saveMailConfiguration(state.mailConfiguration);
      }
      commit.setIsLoading(false);
    },
    async createMailBoxConfiguration(context) {
      const { commit, state } = _moduleActionContext(context);
      commit.setIsLoading(true);
      if (state.mailConfiguration !== null) {
        await createMailConfiguration(state.newMailConfiguration);
      }
      commit.setIsLoading(false);
    },
    async getLastReadMailUIDMessage(context, param: { connectionType: string }) {
      const { commit, state } = _moduleActionContext(context);
      commit.setIsLoading(true);
      if (state.mailConfiguration !== null) {
        await lastReadMailUIDMessage(state.mailConfiguration.id, param.connectionType);
      }
      commit.setIsLoading(false);
    },
    async runConnectionTest(context, param: { connectionType: string }) {
      const { commit, state } = _moduleActionContext(context);
      commit.setIsLoading(true);
      if (state.mailConfiguration !== null) {
        await runTestConnection(state.mailConfiguration.id, param.connectionType);
      }
      commit.setIsLoading(false);
    },
    async downloadMailBoxList(
      context,
      param: {
        page: number;
        count: number;
      }
    ) {
      const { commit } = _moduleActionContext(context);
      commit.setIsLoading(true);
      const response = await getMailBoxList(param.page, param.count);
      commit.setMailBoxList(response.items);
      commit.setMailBoxListCount(response.totalCount);
      commit.setIsLoading(false);
    },
    async downloadMailConfiguration(
      context,
      param: {
        mailId: number;
      }
    ) {
      const { commit } = _moduleActionContext(context);
      commit.setIsLoading(true);
      const response = await getMailConfiguration(param.mailId);
      commit.setMailConfiguration(response);
      commit.setIsLoading(false);
    },
  },
});

/* eslint-disable */
// noinspection JSUnusedLocalSymbols
export default AdministrationMailBoxConfigurationModule;
const _moduleActionContext = (context: any) =>
  moduleActionContext(context, AdministrationMailBoxConfigurationModule);
